import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Http from "../Common/Http";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { MENU } from "./navigation/menu";
import { filterMenu, findActive } from "./navigation/menuUtils";
import "./layout.css";

const AUTH_PATHS = ["/", "/login"];
const RAIL_STORAGE_KEY = "erp.nav.rail";
const COUNTS_REFRESH_MS = 50000;
const CRM_ROLES = ["admin", "supply_chain", "depot"];
const DESKTOP_QUERY = "(min-width: 992px)";

const readRailPreference = () => {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;

/**
 * Application chrome: top bar + sidebar. Owns the state both share
 * (current user, role, badge counts, collapsed / drawer state).
 */
const AppShell = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAuthPage = AUTH_PATHS.includes(pathname);

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isDepartmentHead, setIsDepartmentHead] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [crmPending, setCrmPending] = useState(0);
  const [rail, setRail] = useState(readRailPreference);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Load the session whenever we enter the app, and drop it on the auth pages so
  // a different user signing in never sees the previous user's menu.
  useEffect(() => {
    if (isAuthPage) {
      setUser(null);
      setRole(null);
      setIsDepartmentHead(false);
      setNotificationCount(0);
      setCrmPending(0);
      return undefined;
    }
    if (role !== null) return undefined;

    let cancelled = false;
    Promise.allSettled([Http.get("/me"), Http.get("/staff-type-check"), Http.get("/login-staff-detail")]).then(
      ([me, staffType, staffDetail]) => {
        if (cancelled) return;
        if (me.status === "fulfilled") setUser(me.value.data.data);
        setRole(staffType.status === "fulfilled" ? staffType.value.data : "");
        if (staffDetail.status === "fulfilled") setIsDepartmentHead(staffDetail.value.data?.department_head === 1);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isAuthPage, role]);

  const refreshCounts = useCallback(() => {
    Http.get("/unread-notification")
      .then((res) => {
        const count = Array.isArray(res.data) ? res.data.length : 0;
        setNotificationCount(count);
        const title = document.getElementById("app-title");
        if (title) title.innerText = count > 0 ? `(${count}) Limtech ERP` : "Limtech ERP";
      })
      .catch(() => {});

    if (CRM_ROLES.includes(role)) {
      Http.get("/crm-pending-count")
        .then((res) => setCrmPending(Number(res.data) || 0))
        .catch(() => setCrmPending(0));
    }
  }, [role]);

  useEffect(() => {
    if (isAuthPage || !role) return undefined;
    refreshCounts();
    const interval = setInterval(refreshCounts, COUNTS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [isAuthPage, role, refreshCounts]);

  // Counts change when the user works through notifications or tickets.
  const previousPath = useRef(pathname);
  useEffect(() => {
    const watched = (path) => path === "/notification" || path === "/crm";
    if (!isAuthPage && role && (watched(previousPath.current) || watched(pathname))) refreshCounts();
    previousPath.current = pathname;
  }, [pathname, isAuthPage, role, refreshCounts]);

  // Some pages add `collapse-menu` / `show-left` on mount to go full width.
  // Reset them on navigation; this effect runs before the next page's own effects.
  useEffect(() => {
    document.body.classList.remove("collapse-menu", "show-left");
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const { classList } = document.body;
    classList.toggle("erp-shell", !isAuthPage);
    classList.toggle("erp-nav-rail", !isAuthPage && rail);
    classList.toggle("erp-nav-open", !isAuthPage && mobileOpen);
  }, [isAuthPage, rail, mobileOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(RAIL_STORAGE_KEY, rail ? "1" : "0");
    } catch {
      // Preference just won't persist.
    }
  }, [rail]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  const toggleNav = useCallback(() => {
    if (!isDesktop()) {
      setMobileOpen((open) => !open);
      return;
    }
    // A page that hid the sidebar for full width: the toggle brings it back.
    if (document.body.classList.contains("collapse-menu")) {
      document.body.classList.remove("collapse-menu");
      return;
    }
    setRail((value) => !value);
  }, []);

  const openNav = useCallback(() => {
    if (isDesktop()) document.body.classList.remove("collapse-menu");
    else setMobileOpen(true);
  }, []);

  const signOut = useCallback(() => {
    localStorage.clear();
    navigate("/login");
  }, [navigate]);

  const menu = useMemo(() => (role === null ? [] : filterMenu(MENU, { role, isDepartmentHead })), [role, isDepartmentHead]);
  const active = useMemo(() => findActive(menu, pathname), [menu, pathname]);
  const badges = useMemo(() => ({ crmPending }), [crmPending]);

  if (isAuthPage) return null;

  return (
    <>
      <Header
        user={user}
        role={role}
        active={active}
        notificationCount={notificationCount}
        navExpanded={mobileOpen || !rail}
        onToggleNav={toggleNav}
        onSignOut={signOut}
      />
      <Sidebar
        menu={menu}
        loading={role === null}
        active={active}
        badges={badges}
        user={user}
        role={role}
        onRequestOpen={openNav}
        onClose={() => setMobileOpen(false)}
      />
    </>
  );
};

export default AppShell;
